/**
 * 영업 메일 유입(`?cm=<historyId>`)을 활동 로그용으로 받아 적는 규칙 — 광고 유입(`ad-touch`)과 같은 모양·같은 보관 규칙.
 *
 * 첫 유입(`first`)은 한 번 적으면 지키고, 마지막 유입(`last`)은 매번 덮는다. 만료는 두지 않는다.
 * 가입 보고용 30일 표식(`outreach-ref` 의 `cm.outreach`)과는 따로 둔다 — 그쪽은 웹훅 귀속, 여기는 활동 로그다.
 */

import { readStorageItem, writeStorageItem, isValidOutreachId, OUTREACH_QUERY_KEY, type OutreachStorage } from 'src/utils/outreach/outreach-ref';

import { clip, utmOf, type AdUtmKey, MAX_URL_LENGTH } from './ad-touch';

/** 보관 키(로컬 저장소) — 첫 유입 · 마지막 유입, 각각 `OutreachTouch` JSON */
export const OUTREACH_FIRST_KEY = 'cm.outreach.first';
export const OUTREACH_LAST_KEY = 'cm.outreach.last';

/** 메일 유입 한 번. 없는 값은 `null` · `at` 은 ISO 문자열 */
export type OutreachTouch = { cm: string } & Record<AdUtmKey, string | null> & {
    landing_url: string;
    referrer: string | null;
    at: string;
  };

/**
 * 진입 주소에서 메일 유입 한 번을 꺼낸다.
 * @returns 모양 맞는 `cm` 이 있으면 유입 · 없거나 불량이면 null
 */
export function parseOutreachTouch(href: string, referrer: string, now: number = Date.now()): OutreachTouch | null {
  let url: URL;
  try {
    url = new URL(href);
  } catch {
    return null;
  }
  const cm = url.searchParams.get(OUTREACH_QUERY_KEY);
  if (!isValidOutreachId(cm)) return null;
  return {
    cm,
    ...utmOf((key) => url.searchParams.get(key)),
    landing_url: clip(href, MAX_URL_LENGTH),
    referrer: referrer ? clip(referrer, MAX_URL_LENGTH) : null,
    at: new Date(now).toISOString(),
  };
}

/**
 * 밖에서 온 값(저장소 JSON · 가입 보고 본문)을 `OutreachTouch` 로 좁힌다 — 브라우저와 서버가 같은 판정을 쓴다.
 * @returns 모양이 틀리면 null
 */
export function toOutreachTouch(value: unknown): OutreachTouch | null {
  if (!value || typeof value !== 'object') return null;
  const v = value as Record<string, unknown>;
  if (!isValidOutreachId(v.cm)) return null;
  if (typeof v.landing_url !== 'string' || typeof v.at !== 'string' || Number.isNaN(Date.parse(v.at))) return null;
  if (v.referrer !== null && typeof v.referrer !== 'string') return null;
  return {
    cm: v.cm,
    ...utmOf((key) => (typeof v[key] === 'string' ? (v[key] as string) : null)),
    landing_url: clip(v.landing_url, MAX_URL_LENGTH),
    referrer: typeof v.referrer === 'string' ? clip(v.referrer, MAX_URL_LENGTH) : null,
    at: v.at,
  };
}

/** 보관된 메일 유입을 읽는다 — 없거나 모양이 틀리면 null */
export function readOutreachTouch(storage: OutreachStorage, which: 'first' | 'last'): OutreachTouch | null {
  const raw = readStorageItem(storage, which === 'first' ? OUTREACH_FIRST_KEY : OUTREACH_LAST_KEY);
  if (!raw) return null;
  try {
    return toOutreachTouch(JSON.parse(raw));
  } catch {
    return null;
  }
}

/**
 * 메일 유입을 보관한다 — 첫 유입은 살아 있는 보관본이 있으면 지키고, 마지막 유입은 늘 덮는다.
 * @returns `isFirstTouch` — 이번 유입이 첫 유입으로 적혔나
 */
export function storeOutreachTouch(storage: OutreachStorage, touch: OutreachTouch): { isFirstTouch: boolean } {
  const json = JSON.stringify(touch);
  const isFirstTouch = !readOutreachTouch(storage, 'first');
  if (isFirstTouch) writeStorageItem(storage, OUTREACH_FIRST_KEY, json);
  writeStorageItem(storage, OUTREACH_LAST_KEY, json);
  return { isFirstTouch };
}
